import React from "react";
import { Button, SelectMenu, Text } from "@zorni/components-lib";
import usePartnerAnalyticsDashboard from "./hooks/usePartnerAnalyticsDashboard";
import KpiCard from "./components/KpiCard";
import EvolutionChart from "./components/EvolutionChart";
import GeoDistribution from "./components/GeoDistribution";
import FunnelChart from "./components/FunnelChart";
import InfoTooltip from "./components/InfoTooltip";
import styles from "./PartnerAnalyticsDashboard.module.css";

// Content sourced from the actual chart data (EvolutionChart/FunnelChart/GeoDistribution,
// usePartnerAnalyticsDashboard) cross-checked against
// https://docs.mondoviva.com/Da/monitoring/#1-tableau-des-valeurs-de-tracking.
const EVOLUTION_INFO = (
  <>
    <p>
      Volume d'événements dans le temps, réparti en 3 catégories : <strong>Visibilité</strong>{" "}
      (impressions et vues de l'annonce), <strong>Marketing</strong> (temps passé, rebonds, avis
      lus) et <strong>Conversion</strong> (pré-réservations, clics réservation, favoris).
    </p>
    <p>
      Les zones sont empilées : la hauteur totale d'un point correspond à la somme des 3
      catégories ce mois/jour-là.
    </p>
    <p>Compte des événements, pas des visiteurs uniques — une même personne peut en générer plusieurs.</p>
  </>
);

const FUNNEL_INFO = (
  <>
    <p>Entonnoir en 4 étapes, du premier contact à la réservation :</p>
    <p>
      <strong>Impressions</strong> (annonce apparue en résultats de recherche) →{" "}
      <strong>Vues</strong> (annonce ouverte) → <strong>Pré-réservations</strong> (arrivée sur
      l'écran récapitulatif) → <strong>Réservations</strong> (clic sur le bouton "Réserver").
    </p>
    <p>
      "Réservations" compte les <strong>clics</strong> sur le bouton de réservation, pas
      nécessairement une réservation confirmée en aval.
    </p>
  </>
);

const GEO_INFO = (
  <>
    <p>Répartition des visiteurs par pays, détecté automatiquement lors de la visite.</p>
    <p>
      L'intensité de la couleur reflète le volume relatif par pays (échelle affichée sous la
      carte, de 0 au maximum observé). Survolez un pays pour son nombre exact et sa part du trafic
      total.
    </p>
  </>
);

const formatDateTime = (date) => {
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const PartnerAnalyticsDashboard = () => {
  const { partnerSummary, kpis, options, filters, actions, uiState } =
    usePartnerAnalyticsDashboard();

  const kpiByKey = React.useMemo(() => {
    return kpis.reduce((acc, kpi) => {
      acc[kpi.key] = kpi;
      return acc;
    }, {});
  }, [kpis]);

  return (
    <section className={styles.page}>
      <div className={styles.container}>
        <header className={styles.pageIntro}>
          <div className={styles.pageIntroTitle}>
            <Text as="h2" size="xxl" weight="bold">
              Analytics Partenaire
            </Text>
          </div>

          <div className={styles.pageIntroMeta}>
            <div className={styles.metaPill}>
              <Text as="span" size="xs" weight="bold" className={styles.metaPillLabel}>
                Période
              </Text>
              <Text as="span" size="xs" color="muted">
                {options.periods.find((p) => p.value === filters.period)?.text ?? filters.period}
              </Text>
            </div>
            <div className={styles.metaPill}>
              <Text as="span" size="xs" weight="bold" className={styles.metaPillLabel}>
                Dernière sync
              </Text>
              <Text as="span" size="xs" color="muted">
                {formatDateTime(uiState.lastRefreshedAt)}
              </Text>
            </div>
          </div>
        </header>

        <div className={styles.filterCard}>
          <div className={styles.filterGroup}>
            <SelectMenu
              activeSearch
              showIcon
              iconName="iconBuildings"
              inputSize="small"
              options={options.establishments}
              value={filters.establishmentId}
              onChange={actions.setEstablishmentId}
            />
            <SelectMenu
              activeSearch
              showIcon
              iconName="iconLineSegments"
              inputSize="small"
              options={options.publications}
              value={filters.publicationId}
              onChange={actions.setPublicationId}
            />
            <SelectMenu
              showIcon
              iconName="iconCalendar"
              inputSize="small"
              options={options.periods}
              value={filters.period}
              onChange={actions.setPeriod}
            />
          </div>

          <Button
            label="Mettre a jour"
            variant="primary"
            size="small"
            iconName="iconSend"
            iconPosition="left"
            onClick={actions.refresh}
            disabled={uiState.isFetching}
          />
        </div>

        <div className={styles.kpiGrid} aria-label="KPI principaux">
          <KpiCard kpi={kpiByKey.bounce} className={styles.kpiBounce} />
          <KpiCard kpi={kpiByKey.time} className={styles.kpiTime} />
          <KpiCard kpi={kpiByKey.review} className={`${styles.kpiStackCompact} ${styles.kpiReview}`} compact />
          <KpiCard kpi={kpiByKey.favorites} className={`${styles.kpiStackCompact} ${styles.kpiFavorites}`} compact />
        </div>

        <div className={styles.analyticsGrid}>
          <article className={`${styles.card} ${styles.cardEvolution}`}>
            <div className={styles.cardHeader}>
              <div>
                <Text as="h3" size="lg" weight="semibold" className={styles.cardTitle}>
                  Activité semestrielle
                </Text>
                <Text as="p" size="xs" color="muted" className={styles.cardSubtitle}>
                  Évolution des événements par catégorie
                </Text>
              </div>
              <InfoTooltip title="Activité semestrielle">{EVOLUTION_INFO}</InfoTooltip>
            </div>
            <EvolutionChart points={partnerSummary.stats.evolution} />
          </article>

          <article className={`${styles.card} ${styles.cardFunnel}`}>
            <div className={styles.cardHeader}>
              <div>
                <Text as="h3" size="lg" weight="semibold" className={styles.cardTitle}>
                  Tunnel de conversion
                </Text>
                <Text as="p" size="xs" color="muted" className={styles.cardSubtitle}>
                  De l’impression à la réservation
                </Text>
              </div>
              <InfoTooltip title="Tunnel de conversion">{FUNNEL_INFO}</InfoTooltip>
            </div>
            <FunnelChart items={partnerSummary.stats.funnel} />
          </article>

          <article className={`${styles.card} ${styles.cardGeo}`}>
            <div className={styles.cardHeader}>
              <div>
                <Text as="h3" size="lg" weight="semibold" className={styles.cardTitle}>
                  Provenance internationale
                </Text>
                <Text as="p" size="xs" color="muted" className={styles.cardSubtitle}>
                  Répartition géographique du trafic
                </Text>
              </div>
              <InfoTooltip title="Provenance internationale">{GEO_INFO}</InfoTooltip>
            </div>
            <GeoDistribution items={partnerSummary.stats.geoDistribution} />
          </article>
        </div>
      </div>
    </section>
  );
};

export default PartnerAnalyticsDashboard;
